import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { Screen } from '~/components/Screen';
import { Card, Eyebrow, Notice, PrimaryButton } from '~/components/ui';
import type { GlassArea } from '~/lib/api';
import { AREA_LABELS, areaTitle } from '~/lib/areas';
import {
  brandingPhoto,
  MAX_TOTAL_BYTES,
  type Photo,
  photosForArea,
  SHOT_LABEL,
  shotsFor,
  shotsTaken,
  totalBytes,
  uncoveredAreas,
} from '~/lib/photos';
import { useTheme } from '~/theme/ThemeProvider';

export interface ReviewScreenProps {
  reference: string;
  vin?: string;
  areas: GlassArea[];
  photos: Photo[];
  busy: boolean;
  error: string | null;
  onRemove: (id: string) => void;
  /** Re-enter the shoot step, optionally at a specific area. */
  onShootArea: (area: GlassArea) => void;
  onCaptureBranding: () => void;
  onEditVin: () => void;
  onSubmit: () => void;
  onBack: () => void;
}

/** The block in megabytes, spelled once — it appears in the meter, the note and the CTA. */
const LIMIT_MB = MAX_TOTAL_BYTES / 1024 / 1024;

/**
 * Step 4: check before sending.
 *
 * The last point at which a wrong answer is still preventable by the person holding the
 * phone. A verdict is held for a human reviewer before release, but review reads the same
 * photographs — a blurred frame is not something a reviewer can recover either.
 *
 * It therefore surfaces quality problems rather than just showing thumbnails: an
 * uncovered area, and any area where no frame had a scale reference, which is the flaw
 * that most often produces a confident wrong measurement.
 */
export function ReviewScreen({
  reference,
  vin,
  areas,
  photos,
  busy,
  error,
  onRemove,
  onShootArea,
  onCaptureBranding,
  onEditVin,
  onSubmit,
  onBack,
}: ReviewScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const uncovered = uncoveredAreas(photos, areas);
  const bytes = totalBytes(photos);
  const overBudget = bytes > MAX_TOTAL_BYTES;
  const pct = Math.min(100, (bytes / MAX_TOTAL_BYTES) * 100);
  const branding = brandingPhoto(photos);
  const ready = areas.length > 0 && uncovered.length === 0 && !overBudget && !busy;

  /*
    Three tiers, switching at 70 % and 90 %. Below 70 the meter is ink and says nothing
    worth reading; above it the note earns its line. The note is NOT derived from the bar —
    a bar read at arm's length in sun is a length, not a number, so the state is also a
    sentence and a colour change rather than a fill alone.
  */
  const meter =
    pct > 90
      ? { fill: c.stateFailed, note: `Near the ${LIMIT_MB} MB cap. Remove a duplicate photo.` }
      : pct > 70
        ? { fill: c.noticeCaveat, note: 'Large upload. Keep the app open until the send finishes.' }
        : {
            fill: c.primary,
            note: `Photos are downscaled before sending. Blocked here at ${LIMIT_MB} MB.`,
          };

  return (
    <Screen>
      <View style={{ paddingHorizontal: space.md, gap: space.xs }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.md }}>
          <Pressable
            onPress={onBack}
            accessibilityRole="button"
            accessibilityLabel="Back"
            hitSlop={12}
            // Sized, not just hit-slopped: the glyph's own box is ~14 pt, so without this
            // the visible target and the 44 pt rule disagree with each other.
            style={{
              width: touch.min,
              height: touch.min,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>‹</Text>
          </Pressable>
          <Text style={[t.bodyLg, { fontWeight: '600', color: c.text, flex: 1 }]}>Review</Text>
          {/* The VIN, where the frame puts it: the one identifier worth carrying here. */}
          <Text style={[t.monoValue, { color: c.dim, paddingRight: space.sm }]}>
            {vin ?? reference}
          </Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={{ padding: space.md, gap: space.md }}
        testID="review-scroll"
      >
        <Text style={[t.body, { color: c.dim }]}>
          Check every photo before you send. A bad photo is the one thing that cannot be undone.
        </Text>

        {areas.map((area) => {
          const shots = photosForArea(photos, area);
          /*
            Missing shots are named, and named from THIS pane's set.

            The scale warning used to fire on any area with no scale photo — which became
            wrong the moment the slot sets landed: a tempered pane is never asked for a
            scale shot, so reporting its absence is a warning about a photograph nobody
            wanted. `shotsFor` is the same function the capture screen asks.
          */
          const wanted = shotsFor(area);
          const have = shotsTaken(photos, area);
          const missing = wanted.filter((sh) => !have.has(sh));
          return (
            <Card key={area} style={{ gap: space.sm }}>
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: space.sm }}>
                <Text style={[t.bodyLg, { fontWeight: '600', color: c.text, flex: 1 }]}>
                  {/* Sentence case here, as the frame sets it; the chips stay lower-case. */}
                  {areaTitle(area)}
                </Text>
                <Text style={[t.body, { color: c.dim }]}>
                  {shots.length === 0
                    ? 'No photos'
                    : `${shots.length} photo${shots.length === 1 ? '' : 's'}`}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: space.md,
                  paddingTop: space.sm,
                }}
              >
                {shots.map((p, i) => (
                  <View key={p.id} style={{ width: 64 }}>
                    <Image
                      source={{ uri: p.uri }}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: radius.md,
                        backgroundColor: c.s3,
                      }}
                    />
                    {/* Tag reads on the photograph, so it is ink-on-white, not a tint. */}
                    <Text
                      style={[
                        t.micro,
                        {
                          position: 'absolute',
                          left: space.xs,
                          bottom: space.xs,
                          fontWeight: '600',
                          color: c.onInverse,
                          backgroundColor: c.inverse,
                          borderRadius: radius.sm,
                          paddingHorizontal: space.xs,
                          overflow: 'hidden',
                        },
                      ]}
                    >
                      {p.shot ? SHOT_LABEL[p.shot] : 'Photo'}
                    </Text>
                    <Pressable
                      testID={`review-remove-${area}-${i}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Delete photo ${i + 1} of ${AREA_LABELS[area]}`}
                      onPress={() => onRemove(p.id)}
                      hitSlop={12}
                      style={{
                        position: 'absolute',
                        top: -space.sm,
                        right: -space.sm,
                        width: 24,
                        height: 24,
                        borderRadius: radius.pill,
                        backgroundColor: c.surface,
                        borderWidth: 1,
                        borderColor: c.border2,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Text style={[t.sub, { fontWeight: '600', color: c.text }]}>✕</Text>
                    </Pressable>
                  </View>
                ))}
                {/*
                  The tile names the shot that is actually missing, and is absent once the
                  set is complete. It used to say "+ Angle" on every area, including ones
                  with no angle slot and ones with every slot filled — an invitation to
                  take a photograph the server would refuse.
                */}
                {missing.length > 0 ? (
                  <Pressable
                    testID={`review-add-${area}`}
                    accessibilityRole="button"
                    accessibilityLabel={`Take the ${SHOT_LABEL[missing[0]]} shot of ${AREA_LABELS[area]}`}
                    onPress={() => onShootArea(area)}
                    hitSlop={8}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: radius.md,
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: c.border2,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text style={[t.sub, { fontWeight: '600', color: c.text }]}>
                      {`+ ${SHOT_LABEL[missing[0]]}`}
                    </Text>
                  </Pressable>
                ) : null}
              </View>

              {shots.length === 0 ? (
                <Notice tone="stateFailed" title="Needs at least one photo.">
                  This area cannot be assessed without one.
                </Notice>
              ) : missing.includes('scale') ? (
                // Scale is the one whose absence changes the verdict rather than the
                // confidence: without it a length is a guess, and length decides repair
                // versus replace. It only reaches here on a pane that HAS a scale slot.
                <Notice title="No scale shot.">
                  Size cannot be measured, so repair or replace becomes a judgement call.
                </Notice>
              ) : missing.length > 0 ? (
                <Notice
                  title={`Missing the ${missing.map((sh) => SHOT_LABEL[sh]).join(' and ')} shot${missing.length > 1 ? 's' : ''}.`}
                >
                  {missing.includes('close')
                    ? 'The damage type is read from the close shot.'
                    : 'Add it so the damage can be placed on the pane.'}
                </Notice>
              ) : (
                <Text style={[t.body, { color: c.dim }]}>Good coverage.</Text>
              )}
            </Card>
          );
        })}

        <Eyebrow>extras — each one unlocks something</Eyebrow>
        <Card style={{ gap: space.md }}>
          {(
            [
              {
                testID: 'review-edit-vin',
                title: vin ? 'VIN captured' : 'No VIN',
                detail: vin
                  ? 'Unlocks make, model and whether ADAS is fitted'
                  : 'Vehicle details stay unknown',
                action: vin ? 'Change' : 'Add',
                onPress: onEditVin,
              },
              {
                testID: 'review-capture-branding',
                title: branding ? 'Branding captured' : 'No glass branding',
                detail: branding ? 'OEM vs aftermarket unlocked' : 'Glass type stays unknown',
                action: branding ? 'Retake' : 'Add',
                onPress: onCaptureBranding,
              },
            ] as const
          ).map((row) => (
            <View
              key={row.testID}
              style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}
            >
              <View style={{ flex: 1 }}>
                <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>{row.title}</Text>
                <Text style={[t.body, { color: c.dim }]}>{row.detail}</Text>
              </View>
              <Pressable
                testID={row.testID}
                onPress={row.onPress}
                accessibilityRole="button"
                accessibilityLabel={`${row.action} — ${row.title}`}
                style={{
                  minHeight: touch.min,
                  paddingHorizontal: space.md,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: c.border2,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={[t.label, { color: c.text }]}>{row.action}</Text>
              </Pressable>
            </View>
          ))}
        </Card>
      </ScrollView>

      {/*
        Meter, failure and submit all live in the footer. The reason submit is off has to
        be on screen at the moment the thumb is on submit — a payload warning three scroll
        positions up is a warning nobody reads.
      */}
      <View
        style={{
          paddingHorizontal: space.md,
          paddingTop: space.md,
          paddingBottom: space.md,
          gap: space.sm,
          backgroundColor: c.surface,
          borderTopWidth: 1,
          borderTopColor: c.border,
        }}
      >
        {error ? (
          <Notice tone="stateFailed" title="Could not send.">
            {error} Nothing is lost — your photos are still on this phone. Retrying reuses the same
            key, so you will not be charged twice or get a duplicate job.
          </Notice>
        ) : null}

        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: space.sm }}>
          <Text style={[t.body, { color: c.text, flex: 1 }]}>
            Upload size · {photos.length} photo{photos.length === 1 ? '' : 's'}
          </Text>
          <Text style={[t.monoValue, { fontSize: 14, lineHeight: 24, color: c.text }]}>
            {(bytes / 1024 / 1024).toFixed(1)} of {LIMIT_MB} MB
          </Text>
        </View>
        {/* The meter never animates: a growing bar reads as upload progress. */}
        <View
          style={{
            height: 8,
            borderRadius: radius.sm,
            backgroundColor: c.s3,
            overflow: 'hidden',
          }}
        >
          <View
            testID="payload-meter-fill"
            style={{ width: `${pct}%`, height: 8, backgroundColor: meter.fill }}
          />
        </View>
        <Text style={[t.sub, { fontWeight: '600', color: meter.fill }]}>{meter.note}</Text>

        <PrimaryButton
          testID="submit-assessment"
          label={
            uncovered.length > 0
              ? uncovered.length === 1
                ? '1 area still needs a photo'
                : `${uncovered.length} areas still need a photo`
              : overBudget
                ? `Over ${LIMIT_MB} MB — remove a photo`
                : 'Submit assessment'
          }
          busy={busy}
          disabled={!ready}
          onPress={onSubmit}
        />
        <Text style={[t.sub, { color: c.dim, textAlign: 'center' }]}>
          Photos stay on this device until the send succeeds. A dropped connection retries without
          re-shooting.
        </Text>
      </View>
    </Screen>
  );
}
